// NEW_FILE_JUSTIFIED: Builder right-column Inspector — section/field config panel
import type {
  IBuilderField,
  IFieldValidationRules,
  IInspectorProps,
  ISectionInspectorProps,
  IFieldInspectorProps,
  ICustomOptionsEditorProps,
  IValidationRuleInputProps,
  INumberRuleProps,
  IBoolRuleProps,
  TOptionsSource,
} from '@/@types/insuranceBuilder';
import { getFieldMeta } from '@/lib/insurance/fieldTypeRegistry';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronDown } from 'lucide-react';

export function Inspector({ selection, sections, dispatch }: IInspectorProps) {
  if (!selection) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Select a section or field to edit its settings.
      </div>
    );
  }

  if (selection.kind === 'section') {
    const section = sections.find(s => s.sectionKey === selection.sectionKey);
    if (!section) return null;
    return <SectionInspector section={section} dispatch={dispatch} />;
  }

  const section = sections.find(s => s.sectionKey === selection.sectionKey);
  const field = section?.fields.find(f => f.fieldKey === selection.fieldKey);
  if (!section || !field) return null;
  return (
    <FieldInspector
      field={field}
      sectionKey={section.sectionKey}
      sections={sections}
      dispatch={dispatch}
    />
  );
}

function SectionInspector({ section, dispatch }: ISectionInspectorProps) {
  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Section
        </h3>

        <div className="space-y-2">
          <Label htmlFor="section-title">Title</Label>
          <Input
            id="section-title"
            value={section.title}
            onChange={e =>
              dispatch({
                type: 'UPDATE_SECTION',
                sectionKey: section.sectionKey,
                patch: { title: e.target.value },
              })
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="section-columns">Columns</Label>
          <Select
            value={String(section.columns)}
            onValueChange={v =>
              dispatch({
                type: 'UPDATE_SECTION',
                sectionKey: section.sectionKey,
                patch: { columns: Number(v) as 1 | 2 | 3 }, // AS_JUSTIFIED: Select passes string; items are '1'|'2'|'3' literals so Number() always produces 1|2|3
              })
            }
          >
            <SelectTrigger id="section-columns">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 column</SelectItem>
              <SelectItem value="2">2 columns</SelectItem>
              <SelectItem value="3">3 columns</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="section-description">Description (optional)</Label>
          <Textarea
            id="section-description"
            value={section.description ?? ''}
            onChange={e =>
              dispatch({
                type: 'UPDATE_SECTION',
                sectionKey: section.sectionKey,
                patch: { description: e.target.value || undefined },
              })
            }
            rows={2}
          />
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="section-collapsible"
            checked={section.collapsible ?? false}
            onCheckedChange={c =>
              dispatch({
                type: 'UPDATE_SECTION',
                sectionKey: section.sectionKey,
                patch: { collapsible: c },
              })
            }
          />
          <Label htmlFor="section-collapsible" className="cursor-pointer">
            Collapsible
          </Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="section-icon">Icon (Lucide name, optional)</Label>
          <Input
            id="section-icon"
            value={section.icon ?? ''}
            placeholder="e.g. car"
            onChange={e =>
              dispatch({
                type: 'UPDATE_SECTION',
                sectionKey: section.sectionKey,
                patch: { icon: e.target.value || undefined },
              })
            }
          />
        </div>
      </div>
    </ScrollArea>
  );
}

function FieldInspector({ field, sectionKey, sections, dispatch }: IFieldInspectorProps) {
  const meta = getFieldMeta(field.fieldType);

  function patch(p: Partial<IBuilderField>) {
    dispatch({ type: 'UPDATE_FIELD', sectionKey, fieldKey: field.fieldKey, patch: p });
  }

  function patchValidation(p: Partial<IFieldValidationRules>) {
    patch({ validation: { ...(field.validation ?? {}), ...p } });
  }

  // For carModels dependsOn picker — list other carBrand fields in the same form
  const candidateBrandFields = sections.flatMap(s =>
    s.fields
      .filter(f => f.optionsSource === 'preset:carBrands' && f.fieldKey !== field.fieldKey)
      .map(f => ({ key: f.fieldKey, label: f.label }))
  );

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Field · {meta.label}
        </h3>

        <div className="space-y-2">
          <Label htmlFor="field-label">Label</Label>
          <Input
            id="field-label"
            value={field.label}
            onChange={e => patch({ label: e.target.value })}
          />
        </div>

        {meta.supportsPlaceholder && (
          <div className="space-y-2">
            <Label htmlFor="field-placeholder">Placeholder</Label>
            <Input
              id="field-placeholder"
              value={field.placeholder ?? ''}
              onChange={e => patch({ placeholder: e.target.value || undefined })}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="field-helpText">Help text (optional)</Label>
          <Textarea
            id="field-helpText"
            value={field.helpText ?? ''}
            onChange={e => patch({ helpText: e.target.value || undefined })}
            rows={2}
          />
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="field-required"
            checked={field.required}
            onCheckedChange={c => patch({ required: c })}
          />
          <Label htmlFor="field-required" className="cursor-pointer">
            Required
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="field-fullwidth"
            checked={field.fullWidth ?? false}
            onCheckedChange={c => patch({ fullWidth: c || undefined })}
          />
          <Label htmlFor="field-fullwidth" className="cursor-pointer">
            Full width (override section grid)
          </Label>
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="field-ask-ai">Ask via AI agent</Label>
            <p className="text-xs text-muted-foreground">
              Voice/chat AI agents will collect this field
            </p>
          </div>
          <Switch
            id="field-ask-ai"
            checked={field.askViaAiAgent === true}
            onCheckedChange={value =>
              dispatch({
                type: 'SET_FIELD_ASK_VIA_AI',
                sectionKey,
                fieldKey: field.fieldKey,
                value,
              })
            }
          />
        </div>

        {meta.supportsOptionsSource && (
          <div className="space-y-2">
            <Label>Options source</Label>
            <Select
              value={field.optionsSource ?? 'custom'}
              onValueChange={v => patch({ optionsSource: v as TOptionsSource })} // AS_JUSTIFIED: Select passes string; items are TOptionsSource literals so cast is always valid
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom options</SelectItem>
                <SelectItem value="preset:countries">Preset: Countries</SelectItem>
                <SelectItem value="preset:occupations">Preset: Occupations</SelectItem>
                <SelectItem value="preset:carBrands">Preset: Car brands</SelectItem>
                <SelectItem value="preset:carModels">Preset: Car models</SelectItem>
              </SelectContent>
            </Select>

            {field.optionsSource === 'custom' && (
              <CustomOptionsEditor
                field={field}
                onChange={opts => patch({ customOptions: opts })}
              />
            )}

            {field.optionsSource === 'preset:carModels' && (
              <div className="space-y-1.5 pt-2">
                <Label htmlFor="depends-on">Depends on (Car Brand field)</Label>
                <Select
                  value={field.dependsOnFieldKey ?? ''}
                  onValueChange={v => patch({ dependsOnFieldKey: v })}
                >
                  <SelectTrigger id="depends-on">
                    <SelectValue placeholder="Pick a Car Brand field…" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidateBrandFields.map(c => (
                      <SelectItem key={c.key} value={c.key}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {meta.applicableValidation.length > 0 && (
          <Collapsible>
            <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium hover:underline">
              Validation rules <ChevronDown className="w-4 h-4" />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-3">
              {meta.applicableValidation.map(rule => (
                <ValidationRuleInput
                  key={rule}
                  rule={rule}
                  validation={field.validation ?? {}}
                  onChange={patchValidation}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </ScrollArea>
  );
}

function CustomOptionsEditor({ field, onChange }: ICustomOptionsEditorProps) {
  const opts = field.customOptions ?? [];
  return (
    <div className="space-y-2 pt-2">
      <Label>Options (one per line)</Label>
      <Textarea
        value={opts.join('\n')}
        onChange={e =>
          onChange(
            e.target.value
              .split('\n')
              .map(s => s.trim())
              .filter(Boolean)
          )
        }
        rows={Math.max(3, opts.length)}
        placeholder={'Option 1\nOption 2'}
      />
    </div>
  );
}

function ValidationRuleInput({ rule, validation, onChange }: IValidationRuleInputProps) {
  switch (rule) {
    case 'minLength':
      return (
        <NumberRule
          label="Minimum length"
          value={validation.minLength}
          onChange={v => onChange({ minLength: v })}
        />
      );
    case 'maxLength':
      return (
        <NumberRule
          label="Maximum length"
          value={validation.maxLength}
          onChange={v => onChange({ maxLength: v })}
        />
      );
    case 'min':
      return (
        <NumberRule label="Min value" value={validation.min} onChange={v => onChange({ min: v })} />
      );
    case 'max':
      return (
        <NumberRule label="Max value" value={validation.max} onChange={v => onChange({ max: v })} />
      );
    case 'integerOnly':
      return (
        <BoolRule
          label="Integer only"
          value={validation.integerOnly}
          onChange={v => onChange({ integerOnly: v })}
        />
      );
    case 'regex':
      return (
        <div className="space-y-1.5">
          <Label>Regex pattern</Label>
          <Input
            value={validation.regex ?? ''}
            onChange={e => onChange({ regex: e.target.value || undefined })}
            placeholder="^[0-9]{5}$"
          />
          <Label>Error message (optional)</Label>
          <Input
            value={validation.regexMessage ?? ''}
            onChange={e => onChange({ regexMessage: e.target.value || undefined })}
            placeholder="Must be a 5-digit code"
          />
        </div>
      );
    case 'minDate':
    case 'maxDate':
      return (
        <div className="space-y-1.5">
          <Label>{rule === 'minDate' ? 'Earliest date' : 'Latest date'}</Label>
          <Input
            type="date"
            value={(validation as Record<string, string | undefined>)[rule] ?? ''} // AS_JUSTIFIED: dynamic rule key ('minDate'|'maxDate') cannot be narrowed by TS to keyof IFieldValidationRules at runtime; Record cast is safe because both keys exist on the interface as string|undefined
            onChange={e => onChange({ [rule]: e.target.value || undefined })}
          />
        </div>
      );
    case 'mustBePast':
      return (
        <BoolRule
          label="Must be in past"
          value={validation.mustBePast}
          onChange={v => onChange({ mustBePast: v })}
        />
      );
    case 'mustBeFuture':
      return (
        <BoolRule
          label="Must be in future"
          value={validation.mustBeFuture}
          onChange={v => onChange({ mustBeFuture: v })}
        />
      );
    case 'maxSizeMB':
      return (
        <NumberRule
          label="Max file size (MB)"
          value={validation.maxSizeMB}
          onChange={v => onChange({ maxSizeMB: v })}
        />
      );
    case 'acceptedMimeTypes':
      return (
        <div className="space-y-1.5">
          <Label>Accepted MIME types (comma-separated)</Label>
          <Input
            value={(validation.acceptedMimeTypes ?? []).join(', ')}
            onChange={e =>
              onChange({
                acceptedMimeTypes: e.target.value
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean),
              })
            }
            placeholder="application/pdf, image/*"
          />
        </div>
      );
    default:
      return null;
  }
}

function NumberRule({ label, value, onChange }: INumberRuleProps) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      />
    </div>
  );
}

function BoolRule({ label, value, onChange }: IBoolRuleProps) {
  return (
    <div className="flex items-center gap-2">
      <Switch checked={!!value} onCheckedChange={onChange} />
      <Label className="cursor-pointer">{label}</Label>
    </div>
  );
}
